/**
 * The exam format's review gate (design v2 screen 7): what was learned from the past papers,
 * laid out as the cover box it will produce — board, title, time, total, marks per question,
 * command words, kinds of question by marks — with the past papers' questions beside it. Every
 * part says, in one line, what it changes in a paper (bug fix 1, spec 9). Shares are edited as
 * marks of the paper (API_CHANGES 7) and saved as shares; questions are never removed, only
 * taken out of the patterns, so the copy check keeps them all.
 */
import type { ApiFailure } from "../api/client";
import { FailedPage, LoadingPage, transient } from "../states/Page";
import { useEffect, useState } from "react";
import type {
  Api,
  Exemplar,
  PastPaperListing,
  Profile,
  ProfilePut,
  QuestionTypeShare,
} from "../api/api";
import { describeProfile } from "../papers/describe";
import "../styles/review.css";
import { validate, type Issue } from "./validate";
import { apiMessage } from "../text/errors";
import { commandCounts, definition } from "./commands";
import { duration, plainNote, splitLine } from "./notes";
import { PastQuestions } from "./PastQuestions";
import { byPaper, splitFor } from "./summary";

type Props = {
  api: Api;
  subjectId: string;
  /** Saved (and, for a draft, confirmed): back to the subject. */
  onConfirmed: () => void;
};

type Draft = Omit<ProfilePut, "question_types" | "exemplars" | "per_question_range"> & {
  types: { type: string; typical_marks: number; marks: number }[];
  range: [number, number];
  exemplars: Exemplar[];
};

/** Shares as whole marks that add up to the total (largest remainder): rounded one by one,
 * the learned AQA shares (.2, .25, .2, .2, .15 of 90) made 91 and the draft unconfirmable. */
export function marksOf(shares: number[], total: number): number[] {
  const exact = shares.map((s) => s * total);
  const marks = exact.map(Math.floor);
  const left = total - marks.reduce((n, m) => n + m, 0);
  // Shares far from 1 are the student's to fix, and validate says so; round them plainly.
  if (left < 0 || left > shares.length) return exact.map(Math.round);
  const order = exact.map((x, i) => [x - Math.floor(x), i] as const).sort((a, b) => b[0] - a[0]);
  for (const [, i] of order.slice(0, left)) marks[i]! += 1;
  return marks;
}

function draftOf(p: Profile): Draft {
  const [lo = 1, hi = p.total_marks] = p.per_question_range;
  const marks = marksOf(
    p.question_types.map((q) => q.share),
    p.total_marks,
  );
  return {
    board: p.board,
    paper_title: p.paper_title,
    command_words: [...p.command_words],
    types: p.question_types.map((q, i) => ({
      type: q.type,
      typical_marks: q.typical_marks,
      marks: marks[i]!,
    })),
    total_marks: p.total_marks,
    duration_minutes: p.duration_minutes,
    range: [lo, hi],
    exemplars: [...p.exemplars],
  };
}

function toPut(d: Draft, original: Profile): ProfilePut {
  const total = d.total_marks > 0 ? d.total_marks : 1;
  const question_types: QuestionTypeShare[] = d.types.map((t) => ({
    type: t.type,
    typical_marks: t.typical_marks,
    share: Math.round((t.marks / total) * 10_000) / 10_000,
  }));
  return {
    board: d.board.trim(),
    paper_title: d.paper_title.trim(),
    command_words: d.command_words.map((w) => w.trim()).filter(Boolean),
    question_types,
    total_marks: d.total_marks,
    duration_minutes: d.duration_minutes,
    per_question_range: d.range,
    // The whole list, each marked: only sent when a mark changed. Never shorter (spec 9 §7).
    ...(d.exemplars.some((e, i) => isPattern(e) !== isPattern(original.exemplars[i]))
      ? { exemplars: d.exemplars.map((e) => ({ ...e, pattern: isPattern(e) })) }
      : {}),
  };
}

const isPattern = (e: Exemplar | undefined) => e?.pattern !== false;

const label = (type: string) => {
  const s = type.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function sumLine(sum: number, total: number): { text: string; ok: boolean } {
  if (sum === total) return { text: `Adds up to ${total} ✓`, ok: true };
  const off = Math.abs(sum - total);
  return {
    text: `Adds up to ${sum} — ${off} ${sum > total ? "more" : "fewer"} than ${total}`,
    ok: false,
  };
}

export function ProfileScreen({ api, subjectId, onConfirmed }: Props) {
  const [profile, setProfile] = useState<Profile | "missing" | null>(null);
  const [edit, setEdit] = useState<Draft | null>(null);
  const [source, setSource] = useState<{ filename: string; pages: number | null } | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [word, setWord] = useState("");
  const [listing, setListing] = useState<PastPaperListing | null>(null);
  const [card, setCard] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<ApiFailure | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    void api.getProfile(subjectId).then((r) => {
      // A 404 is "no format yet"; a blip is not, and says so.
      if (!r.ok && transient(r)) return setFailed(r);
      setFailed(null);
      setProfile(r.ok ? r.data : "missing");
      if (!r.ok) return;
      setEdit(draftOf(r.data));
      const first = r.data.source_document_ids[0];
      if (first)
        void api.getDocument(first).then((d) => {
          if (d.ok) setSource({ filename: d.data.filename, pages: d.data.page_count });
        });
      void api.listPastPapers(subjectId).then((l) => {
        if (l.ok) setListing(l.data);
      });
      void api.listSubjects().then((s) => {
        if (s.ok) setSubject(s.data.subjects.find((x) => x.id === subjectId)?.name ?? null);
      });
    });
  }, [api, subjectId, attempt]);

  if (failed) return <FailedPage failure={failed} onRetry={() => setAttempt((n) => n + 1)} />;
  if (profile === null) return <LoadingPage />;
  if (profile === "missing") {
    return (
      <main className="review">
        <h1 className="review__title">No exam format yet</h1>
        <p className="review__lede">
          Upload one of your board's past papers and it is learned from that.
        </p>
      </main>
    );
  }
  if (profile.status === "failed" || edit === null) {
    return (
      <main className="review">
        <h1 className="review__title">The exam format could not be learned</h1>
        <p className="review__lede">{describeProfile(profile, false)}</p>
        <button className="button" disabled={busy} onClick={() => void relearn()}>
          Re-learn from the past papers
        </button>
        {error && (
          <p role="alert" className="field-error">
            {error}
          </p>
        )}
      </main>
    );
  }
  const isDraft = profile.status === "draft";
  const known = profile;

  function patch(next: Partial<Draft>) {
    setEdit((e) => (e ? { ...e, ...next } : e));
  }
  function setMarks(i: number, marks: number) {
    setEdit((e) =>
      e ? { ...e, types: e.types.map((t, j) => (j === i ? { ...t, marks } : t)) } : e,
    );
  }
  function addWord() {
    const w = word.trim();
    if (w && edit && !edit.command_words.includes(w))
      patch({ command_words: [...edit.command_words, w] });
    setWord("");
    setAdding(false);
  }

  async function save(confirm: boolean) {
    if (!edit) return;
    setError(null);
    const body = toPut(edit, known);
    const found = validate(body);
    setIssues(found);
    if (found.length > 0) return;
    setBusy(true);
    try {
      const saved = await api.putProfile(subjectId, body);
      if (!saved.ok) return setError(apiMessage(saved.error));
      if (confirm) {
        const c = await api.confirmProfile(subjectId);
        if (!c.ok) return setError(apiMessage(c.error));
      }
      onConfirmed();
    } finally {
      setBusy(false);
    }
  }

  async function relearn() {
    setError(null);
    setBusy(true);
    try {
      const r = await api.relearnProfile(subjectId);
      if (!r.ok) return setError(apiMessage(r.error));
      setProfile(r.data);
      setEdit(draftOf(r.data));
      setIssues([]);
    } finally {
      setBusy(false);
    }
  }

  function setPattern(x: Exemplar, pattern: boolean) {
    setEdit((e) =>
      e
        ? {
            ...e,
            exemplars: e.exemplars.map((y) =>
              y === x ? { ...y, pattern: pattern ? undefined : false } : y,
            ),
          }
        : e,
    );
  }

  const notes = (profile.provenance.notes ?? []).flatMap((n) => plainNote(n) ?? []);
  const sum = edit.types.reduce((n, t) => n + t.marks, 0);
  const line = sumLine(sum, edit.total_marks);
  const widest = Math.max(edit.total_marks, sum, 1);
  const papers = byPaper(edit.exemplars).length || profile.source_document_ids.length;
  const counts = commandCounts(edit.exemplars, edit.command_words);
  const opened = counts.find((c) => c.word === card) ?? null;
  const split = splitLine(splitFor(edit.types));
  const bad = new Set(issues.map((i) => i.field));

  return (
    <main className="review profile">
      <header>
        {source && (
          <div className="review__file">
            {source.filename}
            {source.pages ? ` · ${source.pages} pages` : ""}
          </div>
        )}
        <h1 className="review__title">{isDraft ? "Check the exam format" : "The exam format"}</h1>
        <p className="review__lede">
          This is the blueprint for every paper Carrel writes for{" "}
          <strong>{subject ?? "this subject"}</strong>. We learned it from{" "}
          <strong>
            {papers} past paper{papers === 1 ? "" : "s"}
          </strong>
          . Each part below says what it changes in your papers — correct anything that's wrong,
          then confirm.
        </p>
        {notes.length > 0 && (
          <div className="notice notice--quiet profile__notes">
            {notes.map((n) => (
              <div key={n}>{n}</div>
            ))}
          </div>
        )}
      </header>

      <div className="profile__grid">
        <div className="format-cover" role="group" aria-label="The format">
          <div className="format-cover__head">
            <input
              className="format-cover__board"
              aria-label="Board"
              value={edit.board}
              onChange={(e) => patch({ board: e.target.value })}
            />
            <input
              className="format-cover__title"
              aria-label="Paper title"
              value={edit.paper_title}
              onChange={(e) => patch({ paper_title: e.target.value })}
            />
            <p className="format-cover__hint">
              Printed on the cover of each paper, and tells the writer which exam to imitate.
            </p>
          </div>
          <div className="format-cover__cells">
            <label className="format-cover__cell">
              Time allowed
              <span className="format-cover__with-unit">
                <input
                  type="number"
                  min={1}
                  aria-label="Time allowed"
                  aria-invalid={bad.has("duration_minutes") || undefined}
                  value={edit.duration_minutes}
                  onChange={(e) => patch({ duration_minutes: Number(e.target.value) })}
                />
                <span>minutes</span>
              </span>
            </label>
            <label className="format-cover__cell">
              Total marks
              <input
                type="number"
                min={1}
                aria-invalid={bad.has("total_marks") || undefined}
                value={edit.total_marks}
                onChange={(e) => patch({ total_marks: Number(e.target.value) })}
              />
            </label>
            <div className="format-cover__cell">
              <span>Marks per question</span>
              <span className="format-cover__with-unit">
                <input
                  type="number"
                  min={1}
                  aria-label="Lowest marks per question"
                  aria-invalid={bad.has("per_question_range") || undefined}
                  value={edit.range[0]}
                  onChange={(e) => patch({ range: [Number(e.target.value), edit.range[1]] })}
                />
                <span>to</span>
                <input
                  type="number"
                  min={1}
                  aria-label="Highest marks per question"
                  aria-invalid={bad.has("per_question_range") || undefined}
                  value={edit.range[1]}
                  onChange={(e) => patch({ range: [edit.range[0], Number(e.target.value)] })}
                />
              </span>
            </div>
            <p className="format-cover__hint format-cover__hint--cells">
              Time and total are used for <strong>Full</strong> papers: {edit.total_marks} marks in{" "}
              {duration(edit.duration_minutes)}. Short (25 marks, 30 min) and Standard (40 marks, 45
              min) papers keep their own length. No question in a paper will be worth less than{" "}
              <strong>{edit.range[0]}</strong> or more than <strong>{edit.range[1]}</strong> marks.
            </p>
          </div>

          <div className="format-cover__section">
            <span className="format-cover__label">Command words</span>
            <p className="format-cover__hint">
              The instruction word that starts a question. Papers use only these words, so they ask
              for the same kinds of answer your exam does. Removing a word means papers won't ask
              questions that start with it.
            </p>
            <ul className="chips" aria-label="Command words">
              {counts.map(({ word: w, count }) => (
                <li key={w} className={count === 0 ? "chip chip--unseen" : "chip"}>
                  <button
                    type="button"
                    className="chip__word"
                    aria-expanded={card === w}
                    onClick={() => setCard(card === w ? null : w)}
                    onFocus={() => setCard(w)}
                  >
                    {w} <span className="chip__count num">{count}</span>
                  </button>
                  <button
                    type="button"
                    className="chip__remove"
                    aria-label={`Remove ${w}`}
                    onClick={() => {
                      patch({ command_words: edit.command_words.filter((x) => x !== w) });
                      if (card === w) setCard(null);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
              <li>
                {adding ? (
                  <input
                    className="chip chip--input"
                    aria-label="Add a command word"
                    autoFocus
                    value={word}
                    onChange={(e) => setWord(e.target.value)}
                    onBlur={addWord}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addWord();
                      }
                      if (e.key === "Escape") {
                        setWord("");
                        setAdding(false);
                      }
                    }}
                  />
                ) : (
                  <button type="button" className="chip chip--add" onClick={() => setAdding(true)}>
                    + Add
                  </button>
                )}
              </li>
            </ul>
            {opened && (
              <p className="chip-card" role="status">
                <strong>{opened.word}</strong>
                {definition(opened.word) ? ` — ${definition(opened.word)}. ` : " — "}
                {opened.count === 0
                  ? "Not seen in your past papers — remove it unless you expect it."
                  : `${opened.count} question${opened.count === 1 ? "" : "s"} in your past papers.`}
              </p>
            )}
          </div>

          <div className="format-cover__section">
            <span className="format-cover__label">Kinds of question</span>
            <p className="format-cover__hint">
              How a paper's marks are split between kinds of question.
            </p>
            <ul className="styles">
              {edit.types.map((t, i) => (
                <li key={`${t.type}${i}`} className="styles__row">
                  <span className="styles__name">{label(t.type)}</span>
                  <span className="styles__track" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, (t.marks / widest) * 100)}%` }} />
                  </span>
                  <input
                    type="number"
                    min={0}
                    className="styles__marks"
                    aria-label={`Marks for ${label(t.type)}`}
                    value={t.marks}
                    onChange={(e) => setMarks(i, Number(e.target.value))}
                  />
                </li>
              ))}
            </ul>
            <span className={line.ok ? "styles__sum" : "styles__sum styles__sum--off"}>
              {line.text}
            </span>
            {split && <p className="styles__example">{split}</p>}
          </div>
        </div>

        <section className="kept" aria-labelledby="kept">
          <div className="kept__head">
            <h2 id="kept">From your past papers</h2>
          </div>
          <PastQuestions
            exemplars={edit.exemplars}
            words={edit.command_words}
            listing={listing}
            onPattern={setPattern}
          />
        </section>
      </div>

      <footer className="profile__foot">
        <div className="profile__foot-text">
          {issues.length > 0 && (
            <ul className="review__error" role="alert">
              {issues.map((i) => (
                <li key={`${i.field}:${i.message}`}>{i.message}</li>
              ))}
            </ul>
          )}
          {error && (
            <div className="review__error" role="alert">
              {error}
            </div>
          )}
          <p>
            Your next paper will follow this format. You can change it later from the subject page;
            papers already written keep the format they were written with.
          </p>
        </div>
        {!isDraft && (
          <button className="button button--quiet" disabled={busy} onClick={() => void relearn()}>
            Re-learn from the past papers
          </button>
        )}
        <button
          className="button button--primary"
          disabled={busy}
          onClick={() => void save(isDraft)}
        >
          {isDraft ? "Confirm this format" : "Save"}
        </button>
      </footer>
    </main>
  );
}
